import React, {
  useEffect,
  useState,
  useMemo,
  VFC,
  useRef,
  useCallback,
} from "react";
import SnackbarController from "@shared-components/snackBar/SnackbarController";
import GenericTemplate from "@template/index";
import { createFormUpload } from "@utils/index";
import formatDateToString from "@utils/DateFormat";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Paper,
} from "@mui/material";
import messages from "config/messages";
import ModalController from "@shared-components/modal/ModalController";
import LoadingOverlayController from "@shared-components/loading/LoadingOverlayController";
import { getUserInfo } from "@utils/index";
import { downloadXlsx } from "@utils/CSV";
import SelectLabel from "components/atoms/SelectLabel";
import { getMasterType } from "@api/masterType";
import { Colors } from "@template/style";
import {
  HeadCell,
  TableInfiniteScroll,
} from "components/organisms/TableInfiniteScroll";
import { getListActivityBase } from "@api/groupMaster";
import { getListExportLog, getListImportLog } from "@api/log";
import {
  IMasterList,
  LIST_GROUP,
  LIST_ACCOUNT,
  LIST_EXPORT_MASTER,
  LIST_EXPORT_ACCOUNT,
} from "screens/MasterImport/MasterInfo";
import { ActivityBase } from "services/models";

interface ILogItem {
  PK: string;
  SK: string;
  export_account_id: string;
  export_account_name: string;
  export_at: string;
  export_count: string;
  export_data_type: string;
  import_data_type: string;
  import_at: string;
  import_account_id: string;
  import_account_name: string;
  import_count: string;
  activity_base_name: string;
}

const exportHeadCells: HeadCell[] = [
  {
    id: "export_at",
    label: "出力日時",
    width: 100,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "activity_base_name",
    label: "出力拠点",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "export_data_type",
    label: "出力マスタ種類",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "export_account_name",
    label: "出力アカウント名",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "export_count",
    label: "出力データ件数",
    width: 100,
    isIgnoreExtractRuleCell: true,
  },
];

const importHeadCells: HeadCell[] = [
  {
    id: "import_at",
    label: "取込日時",
    width: 100,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "activity_base_name",
    label: "取込拠点",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "import_data_type",
    label: "取込マスタ種類",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "import_account_name",
    label: "取込アカウント名",
    width: 140,
    isIgnoreExtractRuleCell: true,
  },
  {
    id: "import_count",
    label: "取込データ件数",
    width: 100,
    isIgnoreExtractRuleCell: true,
  },
];

const MasterLogListScreen: VFC = () => {
  // ------------------------------------------------------------------
  // 初期化
  // ------------------------------------------------------------------
  const [listActivityBase, setListActivityBase] = useState<ActivityBase[]>([]);
  const [isLogType, setIsLogType] = useState<boolean>(true);
  const [lastKey, setLastKey] = useState<any | null>(null);
  const [loadMore, setLoadMore] = useState<boolean>(true);
  const user_info = getUserInfo();
  const prevArgs = useRef<{
    activityBaseId: string;
    lastKey: string;
    isLogType: boolean;
  } | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [listMaster, setListMaster] = useState<IMasterList[]>([]);
  const [activityBaseId, setActivityBaseId] = useState<string>();
  const [listLog, setListLog] = useState<Array<ILogItem>>([]);
  const ITEMS_PER_PAGE = 30;

  // ------------------------------------------------------------------
  // データ取得
  // ------------------------------------------------------------------
  useEffect(() => {
    const getActivityBaseData = async () => {
      let list_ab = await getListActivityBase("", true);
      setListActivityBase(list_ab);
    };

    if (user_info.location_id) {
      setActivityBaseId(user_info.location_id);
    } else {
      setActivityBaseId("");
      getActivityBaseData();
    }
  }, []);

  useEffect(() => {
    let masterList = listMaster;
    const fetchData = async () => {
      if (activityBaseId !== undefined) {
        if (
          !prevArgs.current ||
          prevArgs.current.activityBaseId !== activityBaseId
        ) {
          masterList = await getMasterTypeList(activityBaseId);
        }
        handleClear();
        await getData(activityBaseId, "", false, isLogType, masterList);
      }
    };
    fetchData();
  }, [activityBaseId, isLogType]);

  const getMasterTypeList = async (activityBaseId: string): Promise<any[]> => {
    var list_account = [...LIST_EXPORT_ACCOUNT, ...LIST_ACCOUNT];

    if (!activityBaseId) {
      setListMaster(list_account);
      return list_account; //リストを返す
    } else {
      LoadingOverlayController.show();
      return await getMasterType(activityBaseId, true)
        .then((res) => {
          var new_list_master = [...LIST_EXPORT_MASTER, ...list_account];
          if (res?.data) {
            const list_user_master = res.data.map((v: any) => ({
              id: v.SK,
              name: v.name,
              group: LIST_GROUP.USER_MASTER.name,
              custom_item: res.data.custom_item,
            }));
            new_list_master = new_list_master.concat(list_user_master);
          }
          setListMaster(new_list_master);
          return new_list_master; //リストを返す
        })
        .finally(() => LoadingOverlayController.hide());
    }
  };

  const getData = async (
    activityBaseId: string | undefined,
    lastKey: string,
    isMerge: boolean,
    isLogType: boolean,
    masterList?: any[],
  ) => {
    //引数が変わらなければ何もしない
    if (
      activityBaseId === undefined ||
      (prevArgs.current &&
        prevArgs.current.activityBaseId === activityBaseId &&
        prevArgs.current.lastKey === lastKey &&
        prevArgs.current.isLogType === isLogType)
    ) {
      return;
    }

    //引数を更新
    prevArgs.current = { activityBaseId, lastKey, isLogType };
    if (isFetching) return;
    setIsFetching(true);
    getListLog(activityBaseId, lastKey, isMerge, masterList);
  };

  const getListLog = async (
    activityBaseId: string,
    lastKey: string,
    isMerge: boolean,
    masterList?: any[],
  ) => {
    LoadingOverlayController.show();
    const currentMasterList = listMaster.length ? listMaster : masterList ?? [];
    const apiFunction = isLogType ? getListExportLog : getListImportLog;
    await apiFunction(activityBaseId, lastKey)
      .then((res) => {
        if (res?.data) {
          setListLog((prevList) => {
            let newListLog = isMerge
              ? [...prevList, ...res.data]
              : [...res.data];
            newListLog = newListLog.map((item) => ({
              ...item,
              display_export_data_type:
                currentMasterList.find((d) => d.id === item?.export_data_type)
                  ?.name || "",
              display_import_data_type:
                currentMasterList.find((d) => d.id === item?.export_data_type)
                  ?.name || "",
              export_at: formatDateToString(item?.export_at, "YMDHms_sl"),
              import_at: formatDateToString(item?.import_at, "YMDHms_sl"),
            }));
            return newListLog;
          });
        }
        if (res?.last_key) {
          setLoadMore(true);
          setLastKey(res.last_key);
        } else {
          setLoadMore(false);
        }
        //  30件まで取得 (APIが返す件数と合わせる)
        if (res?.data.length < ITEMS_PER_PAGE && res.last_key !== "") {
          getListLog(activityBaseId, res.last_key, true, currentMasterList);
        }
      })
      .finally(() => {
        setIsFetching(false);
        LoadingOverlayController.hide();
      });
  };

  const handleClear = () => {
    //初期化
    setListLog([]);
    setLastKey("");
  };

  const showLoadMore = useMemo(() => {
    return Boolean(loadMore && lastKey !== "" && listLog.length > 0);
  }, [listLog, loadMore, lastKey]);

  return (
    <GenericTemplate title="マスタ出力・取込ログ一覧">
      <Box display="flex" justifyContent="center" flexDirection="column">
        <Box>
          {user_info.user_role === "Owner" && (
            <SelectLabel
              label="拠点"
              value={activityBaseId !== undefined ? activityBaseId : ""}
              isIgnoreExtractRuleLabel={false}
              isIgnoreExtractRuleSelect={true}
              sx={{ width: 500 }}
              onChange={(e) => {
                setActivityBaseId(e.target.value);
              }}
              displayEmpty
            >
              <MenuItem value={""} key={0}>
                <em>未選択</em>
              </MenuItem>
              {listActivityBase.map((item) => (
                <MenuItem value={item.SK} key={item.SK}>
                  {item.activity_base_name}
                </MenuItem>
              ))}
            </SelectLabel>
          )}
        </Box>
        <Box>
          <RadioGroup
            name="log_type"
            value={isLogType}
            row
            onChange={(_, value) => {
              setIsLogType(!isLogType);
            }}
          >
            <FormControlLabel
              value={true}
              label="マスタ出力ログ"
              control={<Radio />}
            />
            <FormControlLabel
              value={false}
              label="マスタ取込ログ"
              control={<Radio />}
            />
          </RadioGroup>
        </Box>
        <Box>
          <Paper elevation={0} sx={{ py: 1, height: "70vh" }}>
            {isLogType ? (
              <TableInfiniteScroll
                rows={listLog}
                headCells={exportHeadCells}
                idName="SK"
                maxHeight={"70vh"}
                noWrap
                initialLoad={false}
                hasMore={showLoadMore}
                loadMore={() => {
                  if (showLoadMore) {
                    setTimeout(() => {
                      getData(activityBaseId, lastKey, true, isLogType);
                    }, 300);
                  }
                }}
                message={
                  !isFetching && listLog.length === 0
                    ? messages.COMMON.MSG_NOT_EXIST("ログ")
                    : ""
                }
              />
            ) : (
              <TableInfiniteScroll
                rows={listLog}
                headCells={importHeadCells}
                idName="SK"
                maxHeight={"70vh"}
                noWrap
                hasMore={showLoadMore}
                loadMore={() => {
                  if (showLoadMore) {
                    setTimeout(() => {
                      getData(activityBaseId, lastKey, true, isLogType);
                    }, 300);
                  }
                }}
                message={
                  !isFetching && listLog.length === 0
                    ? messages.COMMON.MSG_NOT_EXIST("ログ")
                    : ""
                }
              />
            )}
          </Paper>
        </Box>
      </Box>
    </GenericTemplate>
  );
};

export default MasterLogListScreen;
